import { useAuthStore } from '../stores/auth';
import { useTrialStore } from '../stores/trial';

const BASE = import.meta.env.VITE_API_URL || '/api';

/** サブドメインからテナントslugを取得（例: abc006.jiayin.jp → abc006） */
export function getSlugFromHostname(): string | null {
  const host = window.location.hostname;
  // {slug}.jiayin.jp 形式
  const match = host.match(/^([a-z0-9][a-z0-9-]*[a-z0-9])\.jiayin\.jp$/i);
  if (match && match[1] !== 'estate') return match[1];
  // モックモード時はlocalhostでも 'ihome' をデフォルトslugとして返す
  if (import.meta.env.VITE_USE_MOCKS === 'true' &&
      (host === 'localhost' || host === '127.0.0.1')) {
    return 'ihome';
  }
  return null;
}

// カスタムドメイン → slug のキャッシュ
let _customDomainSlugCache: string | null | undefined = undefined;

/**
 * カスタムドメインのホスト名から API でslugを解決する。
 * 結果はメモリキャッシュ（ページリロードまで保持）。
 */
async function resolveCustomDomainSlug(): Promise<string | null> {
  if (_customDomainSlugCache !== undefined) return _customDomainSlugCache;
  try {
    const res = await fetch(
      `${BASE}/public/domain-lookup?domain=${encodeURIComponent(window.location.hostname)}`,
    );
    if (!res.ok) { _customDomainSlugCache = null; return null; }
    const data = await res.json() as { slug?: string };
    _customDomainSlugCache = data.slug ?? null;
    return _customDomainSlugCache;
  } catch {
    _customDomainSlugCache = null;
    return null;
  }
}

/**
 * 現在のホスト名からテナント slug を解決する:
 *  1. {slug}.jiayin.jp サブドメイン → 正規表現で即時解決
 *  2. カスタムドメイン → API /public/domain-lookup で解決
 *  3. 解決不能 → null（JWT の tenantSlug にフォールバック）
 */
export async function resolveSlug(): Promise<string | null> {
  const subdomainSlug = getSlugFromHostname();
  if (subdomainSlug) return subdomainSlug;
  return resolveCustomDomainSlug();
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const { token, user } = useAuthStore.getState();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...init?.headers as Record<string, string>,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // サブドメイン or カスタムドメイン → slug を X-Tenant-Slug に設定
  const slugFromHost = getSlugFromHostname();
  if (slugFromHost) {
    headers['X-Tenant-Slug'] = slugFromHost;
  } else if (user?.tenantSlug) {
    // JWT の slug を使用（カスタムドメインでログイン済みの場合）
    headers['X-Tenant-Slug'] = user.tenantSlug;
  }

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  // トライアル残日数ヘッダーを読み取り
  const trialDaysHeader = res.headers.get('X-Trial-Days-Left');
  const trialExpiresHeader = res.headers.get('X-Trial-Expires-At');
  const suspendedHeader = res.headers.get('X-Tenant-Suspended');
  if (trialDaysHeader !== null || suspendedHeader !== null) {
    useTrialStore.getState().update(
      trialDaysHeader !== null ? Number(trialDaysHeader) : null,
      trialExpiresHeader,
      suspendedHeader === 'true',
    );
  }

  if (!res.ok) {
    // API が返した { error: "..." } メッセージがあればそれを使う
    let message = `API ${res.status}: ${res.statusText}`;
    try {
      const body = await res.clone().json();
      if (body && typeof body.error === 'string' && body.error) {
        message = body.error;
      }
    } catch { /* JSON でない/body 空 */ }
    throw new Error(message);
  }
  return res.json();
}
